import React, { useState } from 'react';
import { useSignUp } from '@clerk/clerk-react';
import { useNavigate } from 'react-router-dom';

type Step = 'start' | 'verify';

const inputClass =
  'w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 disabled:opacity-50';
const labelClass = 'block text-xs font-medium text-muted-foreground mb-1.5';
const errorClass = 'mt-1 text-xs text-destructive';
const submitClass =
  'w-full rounded-md bg-primary px-4 py-2.5 text-sm font-semibold text-primary-foreground transition-colors hover:bg-primary/90 disabled:opacity-50';
const cardClass =
  'w-full max-w-[400px] rounded-xl border border-border bg-card p-8 shadow-lg';

const GoogleIcon: React.FC = () => (
  <svg viewBox="0 0 24 24" className="h-4 w-4 shrink-0" aria-hidden="true">
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" />
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
  </svg>
);

interface CustomSignUpProps {
  selectedTierLabel?: string;
  isFreeTier?: boolean;
}

const CustomSignUp: React.FC<CustomSignUpProps> = ({
  selectedTierLabel = 'Observer',
  isFreeTier = true,
}) => {
  const { signUp, setActive, isLoaded } = useSignUp();
  const navigate = useNavigate();

  const [step, setStep] = useState<Step>('start');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  if (!isLoaded || !signUp) return null;

  const clerkError = (err: unknown) =>
    ((err as any)?.errors?.[0]?.longMessage ??
      (err as any)?.errors?.[0]?.message ??
      'Something went wrong');

  const handleGoogle = async () => {
    try {
      await signUp.authenticateWithRedirect({
        strategy: 'oauth_google',
        redirectUrl: `${window.location.origin}/sso-callback`,
        redirectUrlComplete: '/dashboard/onboarding',
      });
    } catch (err) {
      setError(clerkError(err));
    }
  };

  const handleStart = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const result = await signUp.create({ emailAddress: email, password });
      if (result.status === 'complete') {
        await setActive({ session: result.createdSessionId });
        navigate('/dashboard/onboarding');
      } else {
        await signUp.prepareEmailAddressVerification({ strategy: 'email_code' });
        setStep('verify');
      }
    } catch (err) {
      setError(clerkError(err));
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const result = await signUp.attemptEmailAddressVerification({ code });
      if (result.status === 'complete') {
        await setActive({ session: result.createdSessionId });
        navigate('/dashboard/onboarding');
      }
    } catch (err) {
      setError(clerkError(err));
    } finally {
      setLoading(false);
    }
  };

  if (step === 'verify') {
    return (
      <form onSubmit={handleVerify} className={cardClass}>
        <h2 className="mb-1 text-xl font-semibold text-foreground">Verify your email</h2>
        <p className="mb-6 text-sm text-muted-foreground">
          We sent a code to your inbox. Enter it below to continue.
        </p>
        <div className="mb-6">
          <label className={labelClass}>Verification code</label>
          <input
            type="text" inputMode="numeric" autoComplete="one-time-code" required
            value={code} onChange={(e) => setCode(e.target.value)}
            className={inputClass}
          />
        </div>
        {error && <p className={errorClass}>{error}</p>}
        <button type="submit" disabled={loading} className={submitClass}>
          {loading ? 'Verifying…' : 'Continue'}
        </button>
        <button
          type="button"
          onClick={() => { setStep('start'); setError(''); }}
          className="mt-3 w-full rounded-md border border-border px-4 py-2.5 text-sm text-muted-foreground transition-colors hover:text-foreground"
        >
          Back
        </button>
      </form>
    );
  }

  return (
    <form onSubmit={handleStart} className={cardClass}>
      <h2 className="mb-1 text-xl font-semibold text-foreground">
        {isFreeTier ? 'Create your Observer account' : `You selected ${selectedTierLabel}`}
      </h2>
      <p className="mb-6 text-sm text-muted-foreground">
        {isFreeTier
          ? 'Observer is free. No credit card required. Takes about 5 minutes after sign-up: connect your Alpaca paper account, set your limits, review a plan.'
          : `Create your account now. You will confirm ${selectedTierLabel} billing inside the product.`}
      </p>

      <button
        type="button"
        onClick={handleGoogle}
        className="mb-4 flex w-full items-center justify-center gap-2 rounded-md border border-border bg-background px-4 py-2.5 text-sm font-medium text-foreground transition-colors hover:bg-muted"
      >
        <GoogleIcon />
        Continue with Google
      </button>

      <div className="mb-5 flex items-center gap-3 text-xs text-muted-foreground">
        <div className="h-px flex-1 bg-border" />
        <span>or</span>
        <div className="h-px flex-1 bg-border" />
      </div>

      <div className="mb-4">
        <label className={labelClass}>Email address</label>
        <input
          type="email" autoComplete="email" required
          value={email} onChange={(e) => setEmail(e.target.value)}
          className={inputClass}
        />
      </div>

      <div className="mb-6">
        <label className={labelClass}>Password</label>
        <input
          type="password" autoComplete="new-password" required
          value={password} onChange={(e) => setPassword(e.target.value)}
          className={inputClass}
        />
      </div>

      {error && <p className={errorClass}>{error}</p>}

      <button type="submit" disabled={loading} className={submitClass}>
        {loading ? 'Creating account…' : 'Create account'}
      </button>

      <p className="mt-5 text-center text-xs text-muted-foreground">
        Already have an account?{' '}
        <a href="/sign-in" className="text-primary hover:underline">Sign in</a>
      </p>
    </form>
  );
};

export default CustomSignUp;
